import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { Dropdown } from "react-bootstrap";
import {
  LayoutDashboard,
  CheckSquare,
  Calendar,
  Utensils,
  ShoppingCart,
  StickyNote,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  CheckCircle,
  Briefcase,
  MessageSquare,
  GitGraph
} from "lucide-react";
import useStore from "../utils/store";
import logo from "../assets/weeker_ai_logo_darkbg.png";
import FeedbackDialog from "./feedback/FeedbackDialog";

const TopNavLoggedIn = ({ auth, signOutRedirect }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const workspaceType = useStore((state) => state.workspaceType);
  const workspaceName = useStore((state) => state.workspaceName);

  const subscription = useStore((state) => state.subscription);

  const navBg = "#334155";
  const activeIndicator = "#3b82f6";

  // Determine if user has Pro features (Tasks, etc.)
  const isPro = subscription?.plan_name === 'Premium' || subscription?.plan_name === 'Pro';

  const tabs = [
    { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard, mobile: true },
    { name: "Tasks", path: "/tasks", icon: CheckCircle, mobile: true, businessOnly: true, allowPro: true },
    { name: "Listen", path: "/packlisten", icon: CheckSquare, mobile: true },
    { name: "Kalender", path: "/kalender", icon: Calendar, mobile: false },
    { name: "Essen", path: "/Essensplanung", icon: Utensils, mobile: true, homeOnly: true },

    { name: "Einkauf", path: "/ShoppingList", icon: ShoppingCart, mobile: true, homeOnly: true },
    { name: "PLM", path: "/plm", icon: GitGraph, mobile: false, businessOnly: true, allowPro: true },
    { name: "Notizen", path: "/Notes", icon: StickyNote, mobile: false },
    // Removed Workspaces and Settings from here as they are now in the dropdown
    // { name: "Workspaces", path: "/Profile", icon: Users, mobile: false },
    // { name: "Settings", path: "/Settings", icon: Settings, mobile: false },
  ];

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  return (
    <>
      {/* --- TOP NAV (Desktop & Mobile Brand Bar) --- */}
      <nav
        className="d-flex justify-content-between align-items-center px-3 px-md-4 sticky-top shadow"
        style={{
          backgroundColor: navBg,
          zIndex: 1030,
          height: "60px",
          borderBottom: "1px solid rgba(255,255,255,0.1)"
        }}
      >
        {/* Brand */}
        <Link to="/dashboard" className="d-flex align-items-center">
          <img src={logo} alt="WeekerAI" style={{ height: "45px", width: "auto" }} />
        </Link>

        {/* Desktop Tabs (Hidden on Mobile) */}
        <div className="d-none d-lg-flex align-items-stretch h-100">
          {tabs.filter(t => {
            if (workspaceType === 'business') {
              return !t.homeOnly;
            } else {
              // In Home workspace: show if NOT businessOnly OR if it allows Pro and user is Pro
              if (t.businessOnly) {
                return t.allowPro && isPro;
              }
              return true;
            }
          }).map((tab) => {
            const isActive = location.pathname === tab.path;
            const Icon = tab.icon;
            return (
              <Link
                key={tab.name}
                to={tab.path}
                className="d-flex align-items-center gap-2 text-decoration-none px-3 nav-item-hover"
                style={{
                  color: isActive ? "#fff" : "#cbd5e1",
                  borderBottom: isActive ? `3px solid ${activeIndicator}` : "3px solid transparent",
                  transition: "all 0.2s"
                }}
              >
                <Icon size={18} />
                <span className="fw-bold d-none d-xl-block" style={{ fontSize: "0.85rem" }}>{tab.name}</span>
              </Link>
            );
          })}
        </div>

        {/* Right Side: Workspace Dropdown (Desktop) / Menu Toggle (Mobile) */}
        <div className="d-flex align-items-center">

          {/* Desktop Dropdown */}
          <div className="d-none d-md-block">
            <Dropdown align="end">
              <Dropdown.Toggle
                variant="link"
                id="dropdown-basic"
                className="text-decoration-none d-flex align-items-center gap-2 border-0 p-0"
                style={{ color: "#cbd5e1" }}
              >
                <div className="d-flex flex-column align-items-end" style={{ lineHeight: '1.2' }}>
                  <span className="fw-bold text-white" style={{ fontSize: '0.9rem' }}>{workspaceName || 'Workspace'}</span>
                  <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>{auth.user?.profile?.email || 'User'}</span>
                </div>
                <div style={{ backgroundColor: 'rgba(255,255,255,0.1)', padding: '6px', borderRadius: '50%' }}>
                  <Briefcase size={18} className="text-white" />
                </div>
              </Dropdown.Toggle>

              <Dropdown.Menu className="shadow-lg mt-2 border-0" style={{ minWidth: '200px' }}>
                <Dropdown.Item as={Link} to="/Profile" className="d-flex align-items-center gap-2 py-2">
                  <Users size={16} />
                  <span>Workspaces</span>
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/Settings" className="d-flex align-items-center gap-2 py-2">
                  <Settings size={16} />
                  <span>Settings</span>
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setShowFeedback(true)} className="d-flex align-items-center gap-2 py-2">
                  <MessageSquare size={16} />
                  <span>Give Feedback</span>
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={signOutRedirect} className="d-flex align-items-center gap-2 py-2 text-danger">
                  <LogOut size={16} />
                  <span>Logout</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <button
            className="btn text-white d-lg-none p-2 shadow-none"
            onClick={toggleMobileMenu}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* --- MOBILE FULLSCREEN OVERLAY MENU --- */}
      {mobileMenuOpen && (
        <div
          className="position-fixed w-100 h-100 d-lg-none"
          style={{
            backgroundColor: navBg,
            zIndex: 2100,
            top: 0,
            left: 0,
            paddingTop: "80px",
            paddingBottom: "80px",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch"
          }}
        >
          <div className="d-flex flex-column px-4 gap-2">
            <div className="text-white-50 mb-3 px-3">
              <small className="d-block text-uppercase fw-bold" style={{ fontSize: '0.7rem' }}>Current Workspace</small>
              <span className="text-white fw-bold fs-5">{workspaceName}</span>
            </div>

            {tabs.filter(t => {
              if (workspaceType === 'business') {
                return !t.homeOnly;
              } else {
                // In Home workspace: show if NOT businessOnly OR if it allows Pro and user is Pro
                if (t.businessOnly) {
                  return t.allowPro && isPro;
                }
                return true;
              }
            }).map((tab) => {
              const isActive = location.pathname === tab.path;
              const Icon = tab.icon;
              return (
                <Link
                  key={tab.name}
                  to={tab.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="d-flex align-items-center gap-3 text-decoration-none p-3 rounded-3"
                  style={{
                    backgroundColor: isActive ? "rgba(255,255,255,0.1)" : "transparent",
                    color: isActive ? "#fff" : "#cbd5e1",
                  }}
                >
                  <Icon size={24} />
                  <span className="fw-bold fs-5">{tab.name}</span>
                </Link>
              );
            })}

            <hr className="text-white-50" />

            <Link
              to="/Profile"
              onClick={() => setMobileMenuOpen(false)}
              className="d-flex align-items-center gap-3 text-decoration-none p-3 rounded-3"
              style={{ color: "#cbd5e1" }}
            >
              <Users size={24} />
              <span className="fw-bold fs-5">Workspaces</span>
            </Link>
            <Link
              to="/Settings"
              onClick={() => setMobileMenuOpen(false)}
              className="d-flex align-items-center gap-3 text-decoration-none p-3 rounded-3"
              style={{ color: "#cbd5e1" }}
            >
              <Settings size={24} />
              <span className="fw-bold fs-5">Settings</span>
            </Link>

            <button
              onClick={signOutRedirect}
              className="d-flex align-items-center gap-3 text-white-50 bg-transparent border-0 p-3"
            >
              <LogOut size={24} />
              <span className="fw-bold fs-5">Abmelden</span>
            </button>
          </div>
        </div>
      )}

      <FeedbackDialog
        show={showFeedback}
        onHide={() => setShowFeedback(false)}
        auth={auth}
      />

      {/* --- MOBILE BOTTOM NAVIGATION (Tab Bar) --- */}
      <div
        className="d-flex d-lg-none fixed-bottom bg-white border-top justify-content-around align-items-center shadow-lg"
        style={{ height: "65px", zIndex: 1050 }}
      >
        {tabs.filter(t => {
          if (!t.mobile) return false;
          if (workspaceType === 'business') return !t.homeOnly;
          // Home workspace
          if (t.businessOnly) return t.allowPro && isPro;
          return true;
        }).map((tab) => {
          const isActive = location.pathname === tab.path;
          const Icon = tab.icon;
          return (
            <Link
              key={tab.name}
              to={tab.path}
              className="d-flex flex-column align-items-center text-decoration-none justify-content-center h-100"
              style={{
                width: "20%",
                color: isActive ? activeIndicator : "#64748b",
              }}
            >
              <Icon size={22} strokeWidth={isActive ? 2.5 : 2} />
              <span style={{ fontSize: "0.65rem", marginTop: "4px", fontWeight: isActive ? "700" : "500" }}>
                {tab.name}
              </span>
            </Link>
          );
        })}
      </div>

      <style>{`
        .nav-item-hover:hover { 
          background-color: rgba(255,255,255,0.08); 
          color: #fff !important; 
        }
        .hover-text-white:hover { color: #fff !important; }
        /* Remove default dropdown arrow */
        #dropdown-basic::after {
            display: none !important; 
        }
        @media (min-width: 992px) {
          body { padding-bottom: 0; }
        }
      `}</style>
    </>
  );
};

export default TopNavLoggedIn;